import { Div, Flex } from '@fastnorth/kraken/components/layout';
import { Trophy, Users } from 'lucide-react';
import { Text } from '@fastnorth/kraken/components/typography';
import Separator from '@fastnorth/kraken/components/separator';
import type { TextProps } from 'node_modules/@fastnorth/kraken/dist/components/typography/Text';
import { type ReactElement, useMemo } from 'react';
import { useApp } from '../../hooks/AppProvider';
import { useTranslation } from 'react-i18next';
import { NationalityFlag } from '../../views/leaderboard/components/NationalityFlag';
import type { NationalFlagIds, RaceStatus } from '../../config/types';
import type { ResultDetailParticipant } from '../../graphql/rest-link/types';
import type { ParticipantSearchRow } from '../../types/events';
import Badge from '@fastnorth/kraken/components/badge';
import { getRaceStatusLabel } from 'src/utils/getRaceStatusLabel.ts';

type CombinedDetailsItem = { key: string; value: string | number | ReactElement };

export type ParticipantInfoProps = {
  participant: ParticipantSearchRow;
  resultsSubEventId: number;
  includeAward?: boolean;
  includeCategoryLabels?: boolean;
  includeRaceName?: boolean;
  displayNameSize?: TextProps['size'];
  raceStatus?: RaceStatus;
};

const ParticipantInfo = ({
  participant,
  includeAward = false,
  includeCategoryLabels = false,
  resultsSubEventId,
  includeRaceName = false,
  displayNameSize = 'md',
  raceStatus,
}: ParticipantInfoProps): ReactElement | null => {
  const { t } = useTranslation('translation', { keyPrefix: 'karwa.components.search_results' });
  const { t: tRaceResult } = useTranslation('translation');
  const [{ event }] = useApp();

  // Handle different field names from various data sources:
  // - ResultDetailParticipant uses: name, genderLabel, team, bib
  // - OpenSearchSingleResultRow uses: genderSexId, teamName, bib
  // - ParticipantSearchRow uses: displayName, gender, teamName, bibNumber
  //TODO: fixme - participant type
  const participantData = participant as unknown as ResultDetailParticipant & {
    genderSexId?: string | null;
    gender?: string | null;
    hasAward?: boolean;
    teamName?: string | null;
    bibNumber?: string | number | null;
    displayName?: string | null;
  };

  const { age, ageGroup, division, hasAward, nationality, fromLocation } = participantData;

  const displayName = participantData.name ?? participantData.displayName;

  const teamName = participantData.team ?? participantData.teamName;
  const bibNumber = participantData.bib ?? participantData.bibNumber;
  const gender =
    participantData.genderLabel ?? participantData.genderSexId ?? participantData.gender;

  const isParticipantInTeam = displayName && teamName;

  const subEventFromResult = useMemo(() => {
    if (event && includeRaceName) {
      return event!.subEvents.find((subEvent) => `${subEvent.id}` === `${resultsSubEventId}`);
    }
    return null;
  }, [event, includeRaceName, resultsSubEventId]);

  const combinedDetails = (
    [
      nationality
        ? {
            key: 'nationality',
            value: <NationalityFlag countryCode={nationality as keyof typeof NationalFlagIds} />,
          }
        : null,
      subEventFromResult ? { key: 'subEvent', value: subEventFromResult!.name } : null,
      bibNumber ? { key: 'bib', value: t('bib_number', { bibNumber }) } : null,
      gender && gender.toLowerCase() !== 'unknown' ? { key: 'gender', value: gender } : null,
      age ? { key: 'age', value: age } : null,
      includeCategoryLabels && division ? { key: 'division', value: division } : null,
      includeCategoryLabels && ageGroup ? { key: 'ageGroup', value: ageGroup } : null,
      fromLocation ? { key: 'fromLocation', value: fromLocation } : null,
    ] as (CombinedDetailsItem | null)[]
  ).filter((detail): detail is CombinedDetailsItem => detail !== null);

  if (!event) return null;

  return (
    <Flex className={'justify-between items-center'}>
      <Flex className={'flex-col'} gap={'2'}>
        <Flex className={'flex-col'}>
          <Text size={displayNameSize} fontWeight={'semibold'} truncate={'wrap'}>
            {displayName || teamName || ''}
          </Text>
          {isParticipantInTeam && (
            <Flex className={'max-sm:max-w-[190px]'} gap={'4'}>
              <Users role={'presentation'} size={16} strokeWidth={2} className={'flex-shrink-0'} />
              <Text size={'md'} truncate={'fit'}>
                {teamName}
              </Text>
            </Flex>
          )}
        </Flex>
        <Div className={'flex items-center flex-wrap'}>
          {includeAward && hasAward ? (
            <Trophy size={20} className={'mr-4'} role="presentation" aria-hidden="true" />
          ) : null}

          {combinedDetails.map((detail, index) => (
            <Flex key={detail.key} className={'flex items-center'}>
              <Text size={'sm'}>{detail.value}</Text>
              {index < combinedDetails.length - 1 && (
                <Separator orientation="vertical" className="h-5 mx-4 border-border" />
              )}
            </Flex>
          ))}
        </Div>

        {raceStatus && (
          <Badge variant={'outlineLabel'}>{getRaceStatusLabel(raceStatus, tRaceResult)}</Badge>
        )}
      </Flex>
    </Flex>
  );
};

export default ParticipantInfo;
